import type { KnowledgeKind, KnowledgeListItem } from '@fos/shared/contracts/knowledge';
import { KNOWLEDGE_SCOPE_AUDIENCE } from '@fos/shared/labels';
import { toLocalDate } from '@fos/shared/time';
import { FileText, Gavel, Link2, type LucideIcon, NotebookPen } from 'lucide-react';
import { formatDateShort, plural } from '../../lib/format';

/** Formatowanie Wiedzy (Figma 10): podpisy w liście, linia meta notatki, grupy po kliencie. */

export const KNOWLEDGE_KIND_ICONS: Record<KnowledgeKind, LucideIcon> = {
  note: NotebookPen,
  decision: Gavel,
  link: Link2,
  file: FileText,
};

/** „dziś”, „wczoraj” albo „3.10” (Europe/Warsaw). */
export function relativeDay(value: Date | string, now: Date = new Date()): string {
  const day = toLocalDate(value instanceof Date ? value : new Date(value));
  if (day === toLocalDate(now)) return 'dziś';
  if (day === toLocalDate(new Date(now.getTime() - 86_400_000))) return 'wczoraj';
  return formatDateShort(day, now);
}

/** Data decyzji w kolumnie listy (Figma 10: „03.10”), mono. */
export function decisionDay(decidedOn: string | null): string {
  if (!decidedOn) return '—';
  const [, m, d] = decidedOn.split('-');
  return `${d}.${m}`;
}

/** Host adresu linku („figma.com”); niepoprawny adres = cały tekst. */
export function hostOf(url: string): string {
  try {
    return new URL(url).host.replace(/^www\./, '');
  } catch {
    return url;
  }
}

/** Rozmiar pliku: „340 KB”, „1,2 MB”. */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;
}

/** Podpis wiersza w liście (Figma 10: „7 decyzji · dziś”, „PDF · 19.09”). */
export function itemSubtitle(
  item: KnowledgeListItem,
  now: Date = new Date(),
  opts: { project?: boolean } = {},
): string {
  const where = opts.project !== false && item.project ? `${item.project.name} · ` : '';
  switch (item.kind) {
    case 'decision':
      return `${where}decyzja z ${item.decidedOn ? formatDateShort(item.decidedOn, now) : '—'}`;
    case 'link':
      return `${where}link · ${item.url ? hostOf(item.url) : '—'}`;
    case 'file':
      return `${where}${item.file?.typeLabel ?? 'plik'} · ${relativeDay(item.createdAt, now)}`;
    default:
      return `${where}v${item.currentVersion} · ${relativeDay(item.updatedAt, now)}`;
  }
}

/** Linia meta szczegółów (Figma 10: „Wersja 7 · zaktualizowane dziś · widzi: Ty”). */
export function detailMeta(
  item: Pick<KnowledgeListItem, 'kind' | 'scope' | 'currentVersion' | 'decidedOn' | 'updatedAt'>,
  now: Date = new Date(),
): string {
  const parts = [`Wersja ${item.currentVersion}`, `zaktualizowane ${relativeDay(item.updatedAt, now)}`];
  if (item.kind === 'decision' && item.decidedOn) parts.unshift(`Decyzja z ${formatDateShort(item.decidedOn, now)}`);
  parts.push(`widzi: ${KNOWLEDGE_SCOPE_AUDIENCE[item.scope]}`);
  return parts.join(' · ');
}

export interface KnowledgeGroup {
  /** `workspace` albo id klienta */
  key: string;
  label: string;
  color: string | null;
  clientId: string | null;
  items: KnowledgeListItem[];
}

/** Grupy listy (Figma 10): najpierw firma (bez klienta), potem klienci alfabetycznie. */
export function groupByClient(items: readonly KnowledgeListItem[], workspaceLabel: string): KnowledgeGroup[] {
  const firm: KnowledgeGroup = { key: 'workspace', label: workspaceLabel, color: null, clientId: null, items: [] };
  const byClient = new Map<string, KnowledgeGroup>();
  for (const item of items) {
    if (!item.client) {
      firm.items.push(item);
      continue;
    }
    const group = byClient.get(item.client.id) ?? {
      key: item.client.id,
      label: item.client.name,
      color: item.client.color,
      clientId: item.client.id,
      items: [],
    };
    group.items.push(item);
    byClient.set(item.client.id, group);
  }
  const clients = [...byClient.values()].sort((a, b) => a.label.localeCompare(b.label, 'pl'));
  return [firm, ...clients];
}

/** „3 decyzje”, „1 notatka” … */
export function countLabel(n: number, forms: readonly [string, string, string]): string {
  return `${n} ${plural(n, forms)}`;
}

export const DECISION_FORMS = ['decyzja', 'decyzje', 'decyzji'] as const;
export const VERSION_FORMS = ['wersja', 'wersje', 'wersji'] as const;
